/**
 * Walk the registry until a session is live, and never wait forever for anything.
 *
 * Public relays are somebody else's machines. They go down, they rate-limit, they get blocked by a
 * corporate proxy, and none of that produces a tidy error: it produces silence. So every step here
 * is raced against a timer and every strategy gets two budgets, a short one to answer a probe and a
 * longer one to settle into a working session. Exhausting the registry is a clear failure with one
 * attempt record per strategy, which is the difference between "could not reach any signaling
 * relay" and a spinner that never stops.
 *
 * The runner is generic over what a strategy opens, so it can be tested against fake strategies and
 * a fake clock without a browser, a network or WebRTC.
 *
 * A fatal failure short-circuits. Trying the BitTorrent trackers will not make a taken join code
 * free, and retrying a version mismatch just makes the player wait longer for the same sentence.
 */

import { NetError, asNetError } from '@/net/session/sessionErrors';
import type { SignalingStrategy } from '@/net/signaling/signalingContract';

export type AttemptOutcome = 'joined' | 'unreachable' | 'timedOut' | 'failed';

export interface StrategyAttempt {
  readonly strategyId: string;
  readonly outcome: AttemptOutcome;
  readonly detail: string;
  readonly elapsedMs: number;
}

export interface FallbackBudget {
  /** How long a relay network gets to answer a socket. */
  readonly probeTimeoutMs: number;
  /** How long an opened room gets to become a session somebody can play in. */
  readonly settleTimeoutMs: number;
}

export const DEFAULT_FALLBACK_BUDGET: FallbackBudget = {
  probeTimeoutMs: 4000,
  settleTimeoutMs: 8000,
};

/** What the caller does with one strategy. Kept out of the runner so the runner stays testable. */
export interface StrategyRun<T> {
  open(strategy: SignalingStrategy, signal: AbortSignal): Promise<T>;
  /** Resolves once the caller judges the session live. Rejects with a `NetError` otherwise. */
  settle(opened: T, signal: AbortSignal): Promise<void>;
  /** Abandons a strategy that did not settle, before the next one is opened. */
  close(opened: T, reason: string): Promise<void>;
}

export interface FallbackResult<T> {
  readonly opened: T;
  readonly strategy: SignalingStrategy;
  readonly attempts: readonly StrategyAttempt[];
}

export async function runWithFallback<T>(
  strategies: readonly SignalingStrategy[],
  run: StrategyRun<T>,
  budget: FallbackBudget,
  onAttempt: (attempt: StrategyAttempt) => void,
  signal: AbortSignal,
): Promise<FallbackResult<T>> {
  const attempts: StrategyAttempt[] = [];

  const record = (attempt: StrategyAttempt): void => {
    attempts.push(attempt);
    onAttempt(attempt);
  };

  for (const strategy of strategies) {
    const startedAt = performance.now();
    const since = (): number => Math.round(performance.now() - startedAt);
    if (signal.aborted)
      throw new NetError('sessionClosed', 'the lobby was closed while connecting');

    const reachable = await deadline(
      strategy.isReachable(budget.probeTimeoutMs, signal),
      budget.probeTimeoutMs,
      false,
    );
    if (!reachable) {
      record({
        strategyId: strategy.id,
        outcome: 'unreachable',
        detail: 'no relay answered',
        elapsedMs: since(),
      });
      continue;
    }

    let opened: T;
    try {
      opened = await run.open(strategy, signal);
    } catch (cause) {
      const error = asNetError(cause, 'relayUnreachable');
      record({
        strategyId: strategy.id,
        outcome: 'failed',
        detail: error.detail === '' ? error.message : error.detail,
        elapsedMs: since(),
      });
      if (error.fatal) throw error;
      continue;
    }

    const settled = await settleOrClose(run, opened, budget.settleTimeoutMs, signal);
    if (settled === null) {
      record({
        strategyId: strategy.id,
        outcome: 'joined',
        detail: 'connected',
        elapsedMs: since(),
      });
      return { opened, strategy, attempts };
    }

    await run.close(opened, settled.error.code);
    if (settled.lobbyClosed) {
      throw new NetError('sessionClosed', 'the lobby was closed while connecting');
    }
    record({
      strategyId: strategy.id,
      outcome: settled.timedOut ? 'timedOut' : 'failed',
      detail: settled.error.detail === '' ? settled.error.message : settled.error.detail,
      elapsedMs: since(),
    });
    if (settled.error.fatal) throw settled.error;
  }

  throw new NetError('signalingExhausted', summarize(attempts));
}

interface SettleFailure {
  readonly error: NetError;
  readonly timedOut: boolean;
  readonly lobbyClosed: boolean;
}

/**
 * Null means it settled. Otherwise the failure, with whether the budget ran out.
 *
 * The caller's `settle` is raced against the budget rather than trusted to watch the signal it is
 * handed. Something that forgets to would otherwise hang the whole registry on its first entry,
 * which is exactly the failure this file exists to make impossible.
 */
async function settleOrClose<T>(
  run: StrategyRun<T>,
  opened: T,
  timeoutMs: number,
  signal: AbortSignal,
): Promise<SettleFailure | null> {
  const expiry = new AbortController();
  const timer = setTimeout(() => {
    expiry.abort();
  }, timeoutMs);
  const linked = AbortSignal.any([signal, expiry.signal]);

  try {
    await Promise.race([run.settle(opened, linked), rejectOnAbort(linked)]);
    return null;
  } catch (cause) {
    const timedOut = expiry.signal.aborted;
    return {
      error: asNetError(cause, timedOut ? 'relayUnreachable' : 'peerConnectionFailed'),
      timedOut,
      lobbyClosed: signal.aborted,
    };
  } finally {
    clearTimeout(timer);
  }
}

/** Never resolves. Rejects the moment the signal aborts, so a `race` against it has an end. */
function rejectOnAbort(signal: AbortSignal): Promise<never> {
  return new Promise<never>((_resolve, reject) => {
    signal.addEventListener(
      'abort',
      () => {
        reject(new NetError('relayUnreachable', 'the session did not settle in time'));
      },
      { once: true },
    );
  });
}

/** Resolves `fallback` if the promise has not answered in time, so no await here can hang. */
async function deadline<T>(promise: Promise<T>, timeoutMs: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<T>((resolve) => {
    timer = setTimeout(() => {
      resolve(fallback);
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, expiry]);
  } finally {
    clearTimeout(timer);
  }
}

function summarize(attempts: readonly StrategyAttempt[]): string {
  if (attempts.length === 0) return 'no signaling strategy is registered';
  return attempts.map((attempt) => `${attempt.strategyId}: ${attempt.detail}`).join(', ');
}
